import { resolveTemplate, translator } from "@solid-primitives/i18n";
import { type ParentComponent, createContext, createSignal, onMount, useContext } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { detectClientLanguage, detectLanguageFromHeader } from "~/utils/languageDetection";
import type { SupportedLocale } from "./types";

interface I18nContextType {
  currentLanguage: () => SupportedLocale;
  setLanguage: (lang: SupportedLocale) => void;
  t: (key: string, dictionary: Record<string, string>, params?: Record<string, any>) => string;
}

const I18nContext = createContext<I18nContextType>();

export const I18nProvider: ParentComponent = (props) => {
  // Detect initial language based on environment
  const getInitialLanguage = (): SupportedLocale => {
    if (isServer) {
      // SSR: Use Accept-Language header
      const event = getRequestEvent();
      const acceptLanguage = event?.request.headers.get("accept-language") || undefined;
      return detectLanguageFromHeader(acceptLanguage);
    }
    // Client: Use navigator.language
    return detectClientLanguage();
  };

  const [currentLanguage, setCurrentLanguage] = createSignal<SupportedLocale>(getInitialLanguage());

  // Use @solid-primitives/i18n for translation functionality

  const setLanguage = (lang: SupportedLocale) => {
    setCurrentLanguage(lang);

    // Store language preference in localStorage for persistence
    if (!isServer) {
      localStorage.setItem("storytell-language", lang);
    }
  };

  // Create translator function using @solid-primitives/i18n
  const translate = (key: string, dictionary: Record<string, string>, params?: Record<string, any>): string => {
    // Create a translator with the provided dictionary
    const t = translator(() => dictionary, resolveTemplate);

    // Use the translator to get the translation with optional params
    return t(key, params || {});
  };

  // On client mount, check for stored language preference or detect client language
  onMount(() => {
    if (!isServer) {
      const storedLanguage = localStorage.getItem("storytell-language") as SupportedLocale;

      if (storedLanguage && storedLanguage !== currentLanguage()) {
        // Use stored preference if it exists
        setLanguage(storedLanguage);
      } else if (!storedLanguage) {
        // If no stored preference, detect from browser and update if different from server
        const clientDetectedLanguage = detectClientLanguage();
        if (clientDetectedLanguage !== currentLanguage()) {
          setLanguage(clientDetectedLanguage);
        }
      }
    }
  });

  const value: I18nContextType = {
    currentLanguage,
    setLanguage,
    t: translate,
  };

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
};
